const SHORTCODE_SELF_CLOSING_TAGS = new Set(['img', 'video', 'hr', 'br']);

const SIGNATURE_SEPARATOR_REGEX = /\n\n---\s*\n\n/;

const truncateAtWordBoundary = (text = '', limit = 0) => {
  const source = String(text || '');
  const max = Math.max(0, Number(limit) || 0);
  if (!max || source.length <= max) return source;

  const sliced = source.slice(0, max);
  const lastSpace = sliced.lastIndexOf(' ');
  return lastSpace > 0 ? sliced.slice(0, lastSpace) : sliced;
};

const stripExtraneousMarkdown = (text = '') => {
  let clean = String(text || '');
  // Remove liketu wrappers
  clean = clean.replace(/<\/?liketuimages>/gi, '');
  // Remove markdown images
  clean = clean.replace(/!\[([^\]]*)\]\([^)]+\)/gi, '');
  // Remove HTML images
  clean = clean.replace(/<img[^>]*>/gi, '');
  // Remove raw center tags which might be left dangling
  clean = clean.replace(/<\/?center>/gi, '');
  return clean.trim();
};

const stripDanglingShortcodeStart = (text = '') => {
  const source = String(text || '');
  const lastOpen = source.lastIndexOf('[');
  const lastClose = source.lastIndexOf(']');

  if (lastOpen !== -1 && lastOpen > lastClose) {
    return source.slice(0, lastOpen).trimEnd();
  }

  return source;
};

const finishUnclosedShortcodes = (text = '') => {
  const source = stripDanglingShortcodeStart(text);
  const tokenRegex = /\[([a-z][a-z0-9]*)(?:[^\]]*)\]|\[\/([a-z][a-z0-9]*)\]/gi;
  const stack = [];
  let match;

  while ((match = tokenRegex.exec(source)) !== null) {
    const openTag = String(match[1] || '').toLowerCase();
    const closeTag = String(match[2] || '').toLowerCase();

    if (openTag) {
      if (!SHORTCODE_SELF_CLOSING_TAGS.has(openTag)) {
        stack.push(openTag);
      }
      continue;
    }

    if (closeTag) {
      const top = stack[stack.length - 1];
      if (top === closeTag) {
        stack.pop();
      }
    }
  }

  if (!stack.length) return source;

  const closing = stack.reverse().map((tag) => `[/${tag}]`).join('');
  return `${source}${closing}`;
};

const splitSignatureBlock = (text = '') => {
  const source = String(text || '');
  // Use the LAST occurrence — posts often use --- as horizontal rules within their
  // body, so only the final separator before an author sign-off is the signature.
  let lastIndex = -1;
  let match;
  const re = new RegExp(SIGNATURE_SEPARATOR_REGEX.source, 'g');
  while ((match = re.exec(source)) !== null) {
    lastIndex = match.index;
  }
  if (lastIndex === -1) return null;

  return {
    body: source.slice(0, lastIndex),
    signature: source.slice(lastIndex),
  };
};

export const buildSafePreviewText = (text = '', limit = 0, { isFlexus = false } = {}) => {
  const rawSource = String(text || '');
  const source = stripExtraneousMarkdown(rawSource);
  const max = Math.max(0, Number(limit) || 0);

  if (!max || source.length <= max) {
    return {
      text: rawSource,
      isTruncated: false,
      expandedForSignature: false,
    };
  }

  const signature = splitSignatureBlock(source);
  if (isFlexus && signature && signature.body.length <= max) {
    return {
      text: source,
      isTruncated: false,
      expandedForSignature: true,
    };
  }

  const sourceForTruncate = signature ? signature.body : source;
  const clipped = truncateAtWordBoundary(sourceForTruncate, max);
  const safeText = finishUnclosedShortcodes(clipped).trimEnd();

  return {
    text: safeText,
    isTruncated: safeText.length < source.length,
    expandedForSignature: false,
  };
};
